/**
 * Room styles: wallpapers and floors. Everyone starts with the default
 * kopitiam look; every other style is bought once in the shop with Kaki
 * Dollars, kept forever, and can then be applied to any room you own (as often
 * as you like, for free). The catalogue holds the colours and pattern names;
 * the client draws them (see roomArt.ts).
 */

export const DEFAULT_STYLE_ID = 'default';

export type WallPattern =
  | 'kopitiam'
  | 'plain'
  | 'stripes'
  | 'dots'
  | 'gingham'
  | 'brick'
  | 'planks'
  | 'stars'
  | 'peranakan'
  | 'waves'
  | 'hearts';

export interface WallStyle {
  id: string;
  label: string;
  /** Shop section. */
  group: 'paint' | 'pattern' | 'special';
  /** Price in Kaki Dollars; zero for the default. */
  price: number;
  pattern: WallPattern;
  /** Main colour, '#rrggbb'. */
  color: string;
  /** Pattern colour (stripes, dots, mortar, stars...). */
  accent: string;
  /** Colour of the lower wall band, or null for none. */
  wainscot: string | null;
  /** Colour of the line above the band and of the skirting. */
  trim: string;
}

export type FloorPattern =
  'tiles' | 'checker' | 'planks' | 'plain' | 'mosaic' | 'diamond' | 'grass' | 'herringbone';

export interface FloorStyle {
  id: string;
  label: string;
  group: 'wood' | 'tile' | 'carpet' | 'special';
  price: number;
  pattern: FloorPattern;
  /** The two alternating colours. */
  a: string;
  b: string;
  /** Grout, plank gaps and outlines. */
  line: string;
}

const wall = (
  id: string,
  label: string,
  group: WallStyle['group'],
  price: number,
  pattern: WallPattern,
  color: string,
  accent: string,
  wainscot: string | null = null,
  trim = '#8a7a64',
): WallStyle => ({ id, label, group, price, pattern, color, accent, wainscot, trim });

const floor = (
  id: string,
  label: string,
  group: FloorStyle['group'],
  price: number,
  pattern: FloorPattern,
  a: string,
  b: string,
  line: string,
): FloorStyle => ({ id, label, group, price, pattern, a, b, line });

const WALL_LIST: readonly WallStyle[] = [
  wall(
    DEFAULT_STYLE_ID,
    'Kopitiam Tiles',
    'special',
    0,
    'kopitiam',
    '#f3ead8',
    '#4e8a79',
    '#6fb3a0',
    '#3f7d6c',
  ),

  wall('paint_white', 'Fresh White', 'paint', 40, 'plain', '#f6f4ee', '#f6f4ee'),
  wall('paint_cream', 'Warm Cream', 'paint', 40, 'plain', '#f3e6c8', '#f3e6c8'),
  wall('paint_sky', 'Sky Blue', 'paint', 50, 'plain', '#cfe6f5', '#cfe6f5'),
  wall('paint_mint', 'Mint', 'paint', 50, 'plain', '#cdebd9', '#cdebd9'),
  wall('paint_blush', 'Blush Pink', 'paint', 50, 'plain', '#f6d5dc', '#f6d5dc'),
  wall('paint_lavender', 'Lavender', 'paint', 50, 'plain', '#ddd3f0', '#ddd3f0'),
  wall('paint_sun', 'Sunshine', 'paint', 50, 'plain', '#f8e6a0', '#f8e6a0'),
  wall('paint_peach', 'Peach', 'paint', 50, 'plain', '#f8d9c0', '#f8d9c0'),
  wall('paint_sage', 'Sage', 'paint', 60, 'plain', '#c5d6b8', '#c5d6b8'),
  wall('paint_terracotta', 'Terracotta', 'paint', 60, 'plain', '#d98b6a', '#d98b6a'),
  wall('paint_navy', 'Midnight Navy', 'paint', 70, 'plain', '#2f4a7a', '#2f4a7a'),
  wall('paint_charcoal', 'Charcoal', 'paint', 70, 'plain', '#4a4e58', '#4a4e58'),
  wall(
    'paint_teal_band',
    'Cream and Teal',
    'paint',
    90,
    'plain',
    '#f3ead8',
    '#f3ead8',
    '#3f8f8a',
    '#2a6f6a',
  ),
  wall(
    'paint_wood_band',
    'Panelled Study',
    'paint',
    120,
    'plain',
    '#e8dcc0',
    '#e8dcc0',
    '#8a5a34',
    '#5a3a20',
  ),

  wall('stripes_blue', 'Blue Stripes', 'pattern', 90, 'stripes', '#e6f0f8', '#9cc5e8'),
  wall('stripes_pink', 'Pink Stripes', 'pattern', 90, 'stripes', '#fbe8ee', '#f0a8be'),
  wall('stripes_green', 'Green Stripes', 'pattern', 90, 'stripes', '#eaf4e4', '#a8d09a'),
  wall('dots_yellow', 'Sunny Dots', 'pattern', 100, 'dots', '#fdf3c8', '#f0c850'),
  wall('dots_blue', 'Blue Polka', 'pattern', 100, 'dots', '#e4eef8', '#5b9bd5'),
  wall('gingham', 'Red Gingham', 'pattern', 110, 'gingham', '#f8f1f0', '#e88a8a'),
  wall('hearts', 'Little Hearts', 'pattern', 120, 'hearts', '#fdeaf0', '#e8608a'),
  wall('waves', 'Seaside Waves', 'pattern', 130, 'waves', '#d4ecf4', '#7fb7d9'),
  wall('brick_red', 'Red Brick', 'pattern', 140, 'brick', '#b5573f', '#8a3a2a'),
  wall('brick_white', 'Whitewashed Brick', 'pattern', 140, 'brick', '#efece6', '#cfc8bc'),
  wall('planks_wood', 'Wood Planks', 'pattern', 150, 'planks', '#c99562', '#a67849'),
  wall('bamboo', 'Bamboo', 'pattern', 150, 'planks', '#d8c98a', '#a89a5a'),

  wall(
    'stars_night',
    'Starry Night',
    'special',
    200,
    'stars',
    '#1f2a5a',
    '#ffe58a',
    null,
    '#141c40',
  ),
  wall(
    'peranakan',
    'Peranakan Tiles',
    'special',
    260,
    'peranakan',
    '#2f8f83',
    '#f0bf36',
    '#e8608a',
    '#1f5f58',
  ),
  wall(
    'peranakan_pink',
    'Nyonya Pink',
    'special',
    260,
    'peranakan',
    '#f2a6bc',
    '#ffffff',
    '#3f8f8a',
    '#8a3a5a',
  ),
];

const FLOOR_LIST: readonly FloorStyle[] = [
  floor(DEFAULT_STYLE_ID, 'Kopitiam Tiles', 'tile', 0, 'tiles', '#e9dcc3', '#e0d1b3', '#cfbe9d'),

  floor('wood_light', 'Light Oak', 'wood', 60, 'planks', '#dcb888', '#d0aa78', '#b8905e'),
  floor('wood_honey', 'Honey Wood', 'wood', 70, 'planks', '#c99562', '#bd8955', '#a67849'),
  floor('wood_dark', 'Dark Walnut', 'wood', 80, 'planks', '#7a5236', '#6e4830', '#4e3020'),
  floor('wood_grey', 'Driftwood', 'wood', 80, 'planks', '#b8b0a4', '#aca498', '#8e867a'),
  floor(
    'herringbone',
    'Parquet Weave',
    'wood',
    140,
    'herringbone',
    '#c99562',
    '#b5834f',
    '#8a5a34',
  ),

  floor('tile_white', 'White Tiles', 'tile', 50, 'tiles', '#f2f0ea', '#e8e5dd', '#cfcbc0'),
  floor('tile_grey', 'Slate Tiles', 'tile', 60, 'tiles', '#8a9098', '#7e848c', '#646a72'),
  floor('tile_terracotta', 'Terracotta', 'tile', 60, 'tiles', '#d98b6a', '#cf7f5e', '#b0664a'),
  floor('tile_blue', 'Blue Tiles', 'tile', 70, 'tiles', '#8ac0e0', '#7db4d6', '#5e94b8'),
  floor('checker_bw', 'Black and White', 'tile', 90, 'checker', '#f4f2ec', '#2f3038', '#1f2026'),
  floor('checker_red', 'Diner Red', 'tile', 90, 'checker', '#f4f2ec', '#c8503f', '#9a3a2c'),
  floor('marble', 'White Marble', 'tile', 150, 'diamond', '#f2f0ea', '#d8d6d0', '#bcbab2'),
  floor('marble_black', 'Black Marble', 'tile', 170, 'diamond', '#3a3a44', '#2a2a32', '#55555f'),

  floor('carpet_red', 'Red Carpet', 'carpet', 70, 'plain', '#b5433a', '#a83a32', '#8a2e28'),
  floor('carpet_blue', 'Blue Carpet', 'carpet', 70, 'plain', '#4a6fa5', '#41649a', '#34537f'),
  floor('carpet_green', 'Green Carpet', 'carpet', 70, 'plain', '#5f9a5a', '#569050', '#457a42'),
  floor('carpet_grey', 'Grey Carpet', 'carpet', 60, 'plain', '#9a9ca2', '#909298', '#7a7c82'),
  floor('carpet_pink', 'Pink Carpet', 'carpet', 70, 'plain', '#f0a8be', '#e89cb2', '#cc8098'),
  floor('carpet_cream', 'Cream Carpet', 'carpet', 60, 'plain', '#efe6d0', '#e6dcc4', '#cfc4a8'),

  floor('grass', 'Green Lawn', 'special', 50, 'grass', '#5fb85a', '#54ac50', '#3f9a44'),
  floor('sand', 'Beach Sand', 'special', 60, 'grass', '#e8d4a0', '#ddc890', '#c9b078'),
  floor('bamboo_mat', 'Bamboo Mat', 'special', 100, 'planks', '#d8c98a', '#cfc07e', '#a89a5a'),
  floor(
    'peranakan_floor',
    'Peranakan Mosaic',
    'special',
    260,
    'mosaic',
    '#2f8f83',
    '#f0bf36',
    '#1f5f58',
  ),
  floor(
    'peranakan_pink_floor',
    'Nyonya Mosaic',
    'special',
    260,
    'mosaic',
    '#f2a6bc',
    '#3f8f8a',
    '#c8708a',
  ),
];

export const WALL_STYLES: Readonly<Record<string, WallStyle>> = Object.freeze(
  Object.fromEntries(WALL_LIST.map((style) => [style.id, style])),
);
export const FLOOR_STYLES: Readonly<Record<string, FloorStyle>> = Object.freeze(
  Object.fromEntries(FLOOR_LIST.map((style) => [style.id, style])),
);

export const WALL_STYLE_IDS: readonly string[] = WALL_LIST.map((style) => style.id);
export const FLOOR_STYLE_IDS: readonly string[] = FLOOR_LIST.map((style) => style.id);

export type StyleKind = 'wall' | 'floor';

/** A style id is unique across both kinds' shop rows, so ownership is stored as `kind:id`. */
export const styleKey = (kind: StyleKind, id: string): string => `${kind}:${id}`;

export function wallStyle(id: string | null | undefined): WallStyle {
  return (
    id && Object.prototype.hasOwnProperty.call(WALL_STYLES, id)
      ? WALL_STYLES[id]
      : WALL_STYLES[DEFAULT_STYLE_ID]
  )!;
}

export function floorStyle(id: string | null | undefined): FloorStyle {
  return (
    id && Object.prototype.hasOwnProperty.call(FLOOR_STYLES, id)
      ? FLOOR_STYLES[id]
      : FLOOR_STYLES[DEFAULT_STYLE_ID]
  )!;
}

export function isStyleId(kind: StyleKind, id: string): boolean {
  return Object.prototype.hasOwnProperty.call(kind === 'wall' ? WALL_STYLES : FLOOR_STYLES, id);
}

export function stylePrice(kind: StyleKind, id: string): number | null {
  if (!isStyleId(kind, id)) return null;
  return (kind === 'wall' ? WALL_STYLES[id] : FLOOR_STYLES[id])!.price;
}

/** What a room looks like. */
export interface RoomLook {
  wall: string;
  floor: string;
}

/** Wallpapers and floors a player owns, for the decorating window. */
export interface OwnedStyles {
  wall: string[];
  floor: string[];
}
